import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate, useSearchParams} from 'react-router-dom';
import authService from '../../common/services/authService';
import {handleError} from '../../common/utils/errorHandling';
import {
  CenteredButton,
  CenteredForm,
  CenteredHeader,
  CenteredInfo,
  CenteredInputPassword,
  CenteredLayout
} from '../../layout/centered/components';
import MessageBox from '../components/MessageBox';

const ResetPassword: React.FC = () => {
  const {t} = useTranslation();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<'form' | 'loading' | 'success' | 'error'>('form');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const id = searchParams.get('id');
    const token = searchParams.get('token');

    if (!id || !token) {
      setStatus('error');
      setErrorMessage(t('common.pages.resetPassword.errorInvalidLink'));
    }
  }, [searchParams, t]);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setErrorMessage(t('common.pages.resetPassword.errorMismatch'));
      return;
    }

    const id = searchParams.get('id');
    const token = searchParams.get('token');

    if (!id || !token) {
      setStatus('error');
      setErrorMessage(t('common.pages.resetPassword.errorInvalidLink'));
      return;
    }

    setStatus('loading');

    await handleError(
      async () => {
        await authService.resetPasswordWithToken(id, token, password);
        setStatus('success');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      },
      'Password reset failed:',
      (err: any) => {
        setStatus('error');
        const backendError = err?.response?.data?.error || err?.response?.data?.message;
        setErrorMessage(backendError || t('common.pages.resetPassword.errorFailed'));
      }
    );
  };

  return (
    <CenteredLayout sectionId="reset-password-section">
      {status === 'form' && (
        <>
          <CenteredHeader
            title={t('common.pages.resetPassword.title')}
            subtitle={t('common.pages.resetPassword.subtitle')}
          />

          <CenteredForm onSubmit={handleSubmit}>
            <CenteredInputPassword
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('common.pages.resetPassword.newPasswordPlaceholder')}
              label={t('common.pages.resetPassword.newPassword')}
              icon="fa fa-lock"
              feedback={true}
              required
            />

            <CenteredInputPassword
              id="confirmPassword"
              name="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('common.pages.resetPassword.confirmPasswordPlaceholder')}
              label={t('common.pages.resetPassword.confirmPassword')}
              icon="fa fa-lock"
              required
            />

            {errorMessage && (
              <div className="text-red-600 text-sm mt-2">{errorMessage}</div>
            )}

            <CenteredButton
              type="submit"
              label={t('common.pages.resetPassword.resetButton')}
              icon="fa fa-check"
              iconPos="left"
            />
          </CenteredForm>
        </>
      )}

      {status === 'loading' && (
        <>
          <CenteredHeader
            title={t('common.pages.resetPassword.loadingTitle')}
            subtitle={t('common.pages.resetPassword.loadingSubtitle')}
          />
          <MessageBox
            icon="fa fa-spinner fa-spin"
            message={t('common.pages.resetPassword.loadingMessage')}
          />
        </>
      )}

      {status === 'success' && (
        <>
          <CenteredHeader
            title={t('common.pages.resetPassword.successTitle')}
            subtitle={t('common.pages.resetPassword.successSubtitle')}
          />
          <MessageBox
            icon="fa fa-check-circle"
            message={t('common.pages.resetPassword.successMessage')}
          />
          <CenteredInfo className="mt-4">
            {t('common.pages.resetPassword.successRedirect')}
          </CenteredInfo>
        </>
      )}

      {status === 'error' && (
        <>
          <CenteredHeader
            title={t('common.pages.resetPassword.failedTitle')}
            subtitle={t('common.pages.resetPassword.failedSubtitle')}
          />
          <MessageBox
            icon="fa fa-times-circle"
            message={errorMessage}
          />
          <div className="mt-8 text-center">
            <CenteredButton
              type="button"
              label={t('common.pages.resetPassword.goToLogin')}
              icon="fa fa-sign-in-alt"
              iconPos="left"
              onClick={() => navigate('/login')}
            />
          </div>
          <CenteredInfo className="mt-4">
            {t('common.pages.resetPassword.needHelp')}&nbsp;
            <Link
              className="text-gray-800 hover:underline cursor-pointer font-medium"
              to="/about"
            >
              {t('common.pages.resetPassword.contactSupport')}
            </Link>
          </CenteredInfo>
        </>
      )}
    </CenteredLayout>
  );
};

export default ResetPassword;
